import { InboxAdapter, InboxFetchOptions, InboxItem, InboxPage, InboxRowItem, SalesInboxItem, cursorOf } from './types';

export type CombinedInboxItem =
  | (InboxItem & { side: 'purchaser' })
  | (SalesInboxItem & { side: 'sales' });

type Side = CombinedInboxItem['side'];
type Sides = { purchaser: InboxAdapter<InboxItem>; sales: InboxAdapter<SalesInboxItem> };

const SIDES: Side[] = ['purchaser', 'sales'];
const PURCHASER_LABEL = { labelKey: 'inbox.side.supplier', fallback: 'Leverantör' };
const SALES_LABEL = { labelKey: 'inbox.side.buyer', fallback: 'Köpare' };

const EXHAUSTED = '-';
const CURSOR_SEP = '&';

const splitCursor = (cursor?: string): Record<Side, string> => {
  const [purchaser = '', sales = ''] = (cursor ?? '').split(CURSOR_SEP).map(decodeURIComponent);
  return { purchaser, sales };
};
const joinCursor = (cursors: Record<Side, string>) =>
  [cursors.purchaser, cursors.sales].map(encodeURIComponent).join(CURSOR_SEP);

const time = (item: InboxRowItem) => new Date(item.updatedAt).getTime();

const emptyPage = { items: [], hasMore: false };

const fetchSide = async <Item extends InboxRowItem, S extends Side>(
  side: S, adapter: InboxAdapter<Item>, opts: InboxFetchOptions, cursor: string,
): Promise<InboxPage<Item & { side: S }>> => {
  if (cursor === EXHAUSTED) return emptyPage;
  const messageTypes = opts.messageTypes.filter(type => adapter.messageTypes.includes(type));
  const filteredToTypesThisSideLacks = opts.messageTypes.length > 0 && messageTypes.length === 0;
  if (filteredToTypesThisSideLacks) return emptyPage;
  const page = await adapter.fetchPage({ ...opts, messageTypes, cursor: cursor || undefined });
  return { ...page, items: page.items.map(item => ({ ...item, side })) };
};

const tagPage = <Item extends InboxRowItem, S extends Side>(side: S, page: InboxPage<Item>): InboxPage<Item & { side: S }> =>
  ({ ...page, items: page.items.map(item => ({ ...item, side })) });

export const combinedInboxAdapter = (sides: Sides): InboxAdapter<CombinedInboxItem> => {
  // Routes a per-item call to the side the item came from.
  const on = <R>(item: CombinedInboxItem, purchaser: (a: InboxAdapter<InboxItem>, i: InboxItem) => R, sales: (a: InboxAdapter<SalesInboxItem>, i: SalesInboxItem) => R): R =>
    item.side === 'purchaser' ? purchaser(sides.purchaser, item) : sales(sides.sales, item);

  return {
    messageTypes: Array.from(new Set([...sides.purchaser.messageTypes, ...sides.sales.messageTypes])),
    createActivity: item => on(item, (a, i) => a.createActivity(i), (a, i) => a.createActivity(i)),

    groupKey: item => `${item.side}:${on(item, (a, i) => a.groupKey(i), (a, i) => a.groupKey(i))}`,
    counterpart: item => on(item, (a, i) => a.counterpart(i), (a, i) => a.counterpart(i)),
    sideLabel: item => (item.side === 'purchaser' ? PURCHASER_LABEL : SALES_LABEL),

    fetchPage: async opts => {
      const cursors = splitCursor(opts.cursor);
      const [purchaser, sales] = await Promise.all([
        fetchSide('purchaser', sides.purchaser, opts, cursors.purchaser),
        fetchSide('sales', sides.sales, opts, cursors.sales),
      ]);
      const pages: Record<Side, InboxPage<CombinedInboxItem>> = { purchaser, sales };

      const boundary = Math.max(
        -Infinity,
        ...SIDES
          .filter(side => pages[side].hasMore && pages[side].items.length > 0)
          .map(side => time(pages[side].items[pages[side].items.length - 1])),
      );

      const merged = [...purchaser.items, ...sales.items].sort((a, b) => time(b) - time(a));
      const items = merged.filter(item => time(item) >= boundary);
      const heldBack = merged.length - items.length;

      const next = { ...cursors };
      for (const side of SIDES) {
        const page = pages[side];
        const emitted = items.filter(item => item.side === side);
        if (emitted.length < page.items.length) {
          next[side] = emitted.length > 0 ? cursorOf(emitted[emitted.length - 1]) : cursors[side];
        } else {
          next[side] = page.hasMore && page.nextCursor ? page.nextCursor : EXHAUSTED;
        }
      }

      const hasMore = purchaser.hasMore || sales.hasMore || heldBack > 0;
      return hasMore ? { items, hasMore, nextCursor: joinCursor(next) } : { items, hasMore: false };
    },

    fetchGroupPage: async (lastItem, opts) => (lastItem.side === 'purchaser'
      ? tagPage('purchaser', await sides.purchaser.fetchGroupPage(lastItem, opts))
      : tagPage('sales', await sides.sales.fetchGroupPage(lastItem, opts))),

    markRead: item => on(item, (a, i) => a.markRead(i), (a, i) => a.markRead(i)),
    markUnread: item => on(item, (a, i) => a.markUnread(i), (a, i) => a.markUnread(i)),
    refreshBadge: () => { sides.purchaser.refreshBadge(); sides.sales.refreshBadge(); },
    markAllRead: () => Promise.all([sides.purchaser.markAllRead(), sides.sales.markAllRead()]),

    loadThread: item => on(item, (a, i) => a.loadThread(i), (a, i) => a.loadThread(i)),
    sendReply: (item, text, files) => on(item, (a, i) => a.sendReply(i, text, files), (a, i) => a.sendReply(i, text, files)),
    isOwnMessage: (msg, item) => on(item, (a, i) => a.isOwnMessage(msg, i), (a, i) => a.isOwnMessage(msg, i)),
    downloadAttachment: (item, attachment) =>
      on(item, (a, i) => a.downloadAttachment(i, attachment), (a, i) => a.downloadAttachment(i, attachment)),
    conversationLink: item => on(item, (a, i) => a.conversationLink?.(i), (a, i) => a.conversationLink?.(i)),
    isProjectClosed: item => on(item, (a, i) => a.isProjectClosed?.(i) ?? false, (a, i) => a.isProjectClosed?.(i) ?? false),
  };
};
